import { z } from 'zod';
import { CLIENT_CONTACT_FULL_NAME_MAX_LENGTH } from '../clients.js';
import { ContactIdentityRefSchema } from './identity.js';

/**
 * @aiPurpose one client contact surfaced by the lead→client matching dialog
 *   (#2244 FR-2). `matchesIdentifier` is `true` when this contact already owns a
 *   registry identifier equal (by normalized value) to one the lead owns — the
 *   pre-filled "matched existing contact" proposal. Global identifier uniqueness
 *   (INV-2) makes a true match rare in practice (a lead-owned value cannot also
 *   be contact-owned), so the manager usually picks a contact or creates one.
 * @aiPii `fullName` is contact PII; the endpoint already enforces client
 *   visibility, so a caller reaching this shape has a confirmed read grant.
 */
export const MatchCandidateContactSchema = z
  .object({
    id: z.string().uuid(),
    fullName: z.string().min(1),
    matchesIdentifier: z.boolean(),
  })
  .strict();
export type MatchCandidateContact = z.infer<typeof MatchCandidateContactSchema>;

/**
 * @aiPurpose response of `GET /leads/:id/match-candidates?clientId=…` (#2244
 *   FR-2). Compares the lead's identifiers to a target client's active contacts
 *   and returns a pre-filled proposal: bind to `matchedContactId` (when a
 *   contact shares an identifier) OR create a new contact seeded with
 *   `suggestedContactName` + `leadIdentifiers`. Confirming either binds in one
 *   `POST /leads/:id/bind` call.
 * @aiPii `leadIdentifiers[].value` is a raw registry identifier (phone / handle)
 *   and `suggestedContactName` is the lead's draft name — both PII. The endpoint
 *   gates on lead AND client visibility, so this shape only reaches a caller who
 *   already has read access to both.
 */
export const MatchCandidatesResponseSchema = z
  .object({
    leadId: z.string().uuid(),
    clientId: z.string().uuid(),
    /** The lead's own registry identifiers, pre-filling create-contact mode. */
    leadIdentifiers: z.array(ContactIdentityRefSchema),
    /** First client contact sharing an identifier with the lead, else `null`. */
    matchedContactId: z.string().uuid().nullable(),
    contacts: z.array(MatchCandidateContactSchema),
    /** Editable contact name proposal seeded from the lead's draft name. */
    suggestedContactName: z.string(),
  })
  .strict();
export type MatchCandidatesResponse = z.infer<typeof MatchCandidatesResponseSchema>;

/**
 * @aiPurpose confirm-bind to an EXISTING client contact (#2244 FR-3, matched
 *   mode). The lead's registry identifiers transfer onto `contactId` in one
 *   transaction (atomic owner-FK flip); the lead's draft `intake` PII stays as
 *   history.
 */
export const BindLeadMatchedInputSchema = z
  .object({
    mode: z.literal('matched'),
    clientId: z.string().uuid(),
    contactId: z.string().uuid(),
  })
  .strict();

/**
 * @aiPurpose confirm-bind by CREATING a client contact (#2244 FR-3, create
 *   mode). `fullName` is the manager-editable name; the new contact's
 *   identifiers come from the lead (transferred by the owner-FK flip), so the
 *   request carries no identifier — the server derives the contact's phone from
 *   the lead's registry PHONE identity.
 * @aiPii `fullName` is contact PII — redact in logs.
 */
export const BindLeadCreateInputSchema = z
  .object({
    mode: z.literal('create'),
    clientId: z.string().uuid(),
    fullName: z.string().min(1).max(CLIENT_CONTACT_FULL_NAME_MAX_LENGTH),
  })
  .strict();

/**
 * @aiPurpose request body of `POST /leads/:id/bind` (#2244 FR-3). A discriminated
 *   union on `mode`: bind to a chosen existing contact, or create a new one. The
 *   response is the refreshed `LeadListItem` (the bound lead).
 */
export const BindLeadInputSchema = z.discriminatedUnion('mode', [
  BindLeadMatchedInputSchema,
  BindLeadCreateInputSchema,
]);
export type BindLeadInput = z.infer<typeof BindLeadInputSchema>;
